<template>
    <div class="step-wrap">
        <!-- Page header — collapses when keyboard is open -->
        <Transition name="fade-height">
            <div v-if="!inputActive" class="step-header">
                <h1 class="step-h1">Who's in your scene?</h1>
                <p class="step-lead">Pick 1–4 stars to cast.</p>
                <p class="step-sub">Build your pitch. Performers will audition for it.</p>
            </div>
        </Transition>

        <div class="cast-inner">
            <!-- Search + selection header — lives outside the scroll container so iOS
                 doesn't auto-scroll on input focus and push the grid off-screen -->
            <div class="cast-sticky">
                <div class="search-wrap">
                    <span class="search-icon"><Search style="width:14px;height:14px" /></span>
                    <input
                        type="text"
                        placeholder="Search or type a name…"
                        v-model="search"
                        class="search-input"
                        @focus="onInputFocus"
                        @blur="onInputBlur"
                        @keydown.enter="onSearchEnter"
                    />
                </div>

                <!-- Selected avatars -->
                <Transition name="fade-height">
                    <div v-if="hasSelection" class="selected-row">
                        <button
                            v-for="name in selectedCast"
                            :key="name"
                            class="avatar-btn"
                            :title="`Remove ${name}`"
                            @click="$emit('toggleCast', name)"
                        >
                            <div class="avatar-ring ring-selected">
                                <img v-if="photoMap[name]" :src="photoMap[name]" :alt="name" class="avatar-img" />
                                <div v-else class="avatar-initials">{{ initials(name) }}</div>
                            </div>
                            <span class="avatar-label text-brand-light">{{ firstName(name) }}</span>
                        </button>
                        <button
                            v-for="name in customCast"
                            :key="'c-' + name"
                            class="avatar-btn"
                            :title="`Remove ${name}`"
                            @click="$emit('removeCustomCast', name)"
                        >
                            <div class="avatar-ring ring-custom">
                                <span class="avatar-initials">{{ initials(name) || '?' }}</span>
                            </div>
                            <span class="avatar-label text-muted">{{ firstName(name) }}</span>
                        </button>
                        <!-- Empty slot placeholders -->
                        <div
                            v-for="i in remaining"
                            :key="'slot-' + i"
                            class="avatar-slot"
                        >
                            <div class="avatar-ring ring-empty">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            </div>
                            <span class="avatar-label text-muted">open</span>
                        </div>
                        <button class="clear-btn" @click="clearAll">Clear</button>
                    </div>
                </Transition>

                <!-- Shuffle row (no selection) -->
                <Transition name="fade">
                    <div v-if="!hasSelection" class="shuffle-row">
                        <button class="shuffle-btn" :disabled="remaining === 0" @click="handleSurprise">
                            <Shuffle style="width:14px;height:14px" /> {{ lastShuffle.length > 0 ? 'Shuffle again' : 'Surprise me' }}
                        </button>
                        <span class="slots-label">{{ MAX_CAST }} slots available</span>
                    </div>
                </Transition>

                <!-- Add random when partially filled -->
                <div v-if="hasSelection && remaining > 0" class="add-random-row">
                    <button class="add-random-btn" @click="handleSurprise">
                        <Shuffle style="width:14px;height:14px" /> Add another randomly
                    </button>
                    <span class="slots-label">{{ remaining }} {{ remaining === 1 ? 'slot' : 'slots' }} available</span>
                </div>
            </div>

            <div class="cast-scroll hide-scrollbar">

                <!-- Popular shelf -->
                <div v-if="showPopular" class="popular-shelf">
                    <p class="shelf-label">Popular right now</p>
                    <div class="popular-row hide-scrollbar">
                        <button
                            v-for="creator in POPULAR_CREATORS"
                            :key="creator.id"
                            class="popular-item"
                            :disabled="!selectedCast.includes(creator.name) && allSelected.length >= MAX_CAST"
                            @click="$emit('toggleCast', creator.name)"
                        >
                            <div
                                class="popular-avatar"
                                :class="selectedCast.includes(creator.name) ? 'popular-selected' : 'popular-unselected'"
                            >
                                <img :src="creator.photo" :alt="creator.name" class="avatar-img" />
                                <template v-if="selectedCast.includes(creator.name)">
                                    <div class="popular-overlay" />
                                    <div class="popular-check"><Check style="width:12px;height:12px" /></div>
                                </template>
                            </div>
                            <span class="popular-name" :class="selectedCast.includes(creator.name) ? 'text-brand-light' : 'text-muted'">
                {{ firstName(creator.name) }}
              </span>
                        </button>
                    </div>
                    <div class="shelf-divider" />
                    <p class="shelf-label mt-4">All creators</p>
                </div>

                <!-- Zero state — custom add -->
                <div v-if="showZeroState" class="zero-state">
                    <button class="custom-add-btn" @click="addCustom(searchTrimmed)">
                        <Plus style="width:14px;height:14px" /> Add "{{ searchTrimmed }}" as custom cast
                    </button>
                </div>

                <!-- Grid -->
                <div class="creator-grid">
                    <button
                        v-for="creator in sortedFiltered"
                        :key="creator.id"
                        class="creator-card"
                        :class="{
              'creator-disabled': !selectedCast.includes(creator.name) && allSelected.length >= MAX_CAST,
              'creator-dimmed': allSelected.length >= MAX_CAST && !selectedCast.includes(creator.name),
            }"
                        :style="{ transform: selectedCast.includes(creator.name) ? 'scale(0.98)' : 'scale(1)' }"
                        @click="!(!selectedCast.includes(creator.name) && allSelected.length >= MAX_CAST) && $emit('toggleCast', creator.name)"
                    >
                        <img :src="creator.photo" :alt="creator.name" class="creator-img" loading="lazy" />
                        <div class="creator-gradient" />
                        <div v-if="selectedCast.includes(creator.name)" class="creator-ring" />
                        <div v-if="selectedCast.includes(creator.name)" class="creator-check"><Check style="width:12px;height:12px" /></div>
                        <div class="creator-info">
                            <div class="creator-name">{{ creator.name }}</div>
                            <div class="creator-role">{{ creator.knownFor }}</div>
                        </div>
                    </button>
                </div>

            </div><!-- /scroll -->
        </div>

        <!-- CTA -->
        <div class="step-footer">
            <p v-if="error" class="error-msg">Pick at least one cast member to continue.</p>
            <button class="cta-btn" @click="handleNext">Lock in cast</button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Search, Shuffle, Plus, Check } from 'lucide-vue-next'

const props = defineProps({
    selectedCast: Array,
    customCast:   Array,
    performers:   { type: Array, default: null },
    popularPerformers: { type: Array, default: null },
})
const emit = defineEmits(['toggleCast', 'addCustomCast', 'removeCustomCast', 'next', 'update:inputFocused'])

const MAX_CAST = 4

const CREATORS = computed(() =>
    (props.performers ?? []).map(p => ({ id: String(p.id), name: p.name, knownFor: '', photo: p.thumbnail_url }))
)
const POPULAR_CREATORS = computed(() =>
    props.popularPerformers?.length
        ? props.popularPerformers.map(p => ({ id: String(p.id), name: p.name, knownFor: '', photo: p.thumbnail_url }))
        : CREATORS.value.slice(0, 8)
)
const photoMap = computed(() => Object.fromEntries(CREATORS.value.map(c => [c.name, c.photo])))

const search      = ref('')
const inputActive = ref(false)
const error       = ref(false)
const lastShuffle = ref([])

const allSelected   = computed(() => [...props.selectedCast, ...props.customCast])
const hasSelection  = computed(() => allSelected.value.length > 0)
const remaining     = computed(() => Math.max(0, MAX_CAST - allSelected.value.length))
const searchTrimmed = computed(() => search.value.trim())
const showPopular   = computed(() => !search.value && remaining.value > 0)

const filtered = computed(() =>
    CREATORS.value.filter(c => c.name.toLowerCase().includes(search.value.toLowerCase()))
)
const sortedFiltered = computed(() => {
    const order = new Map()
    props.selectedCast.forEach((n, i) => order.set(n.toLowerCase(), i))
    return [...filtered.value].sort((a, b) => {
        const ai = order.has(a.name.toLowerCase()) ? order.get(a.name.toLowerCase()) : Infinity
        const bi = order.has(b.name.toLowerCase()) ? order.get(b.name.toLowerCase()) : Infinity
        return ai - bi
    })
})
const showZeroState = computed(() =>
    searchTrimmed.value.length > 0 &&
    filtered.value.length === 0 &&
    allSelected.value.length < MAX_CAST &&
    !allSelected.value.some(n => n.toLowerCase() === searchTrimmed.value.toLowerCase())
)

watch(hasSelection, v => { if (v) error.value = false })

function firstName(name) { return name.split(' ')[0] }
function initials(name) {
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
}

function onInputFocus()  { emit('update:inputFocused', true);  inputActive.value = true  }
function onInputBlur()   { emit('update:inputFocused', false); inputActive.value = false }

function onSearchEnter() {
    const q = searchTrimmed.value
    if (!q) return
    const exact = CREATORS.value.find(c => c.name.toLowerCase() === q.toLowerCase())
    if (exact) {
        if (!props.selectedCast.includes(exact.name) && allSelected.value.length < MAX_CAST)
            emit('toggleCast', exact.name)
        search.value = ''; return
    }
    const liveFiltered = CREATORS.value.filter(c => c.name.toLowerCase().includes(q.toLowerCase()))
    const already = allSelected.value.some(n => n.toLowerCase() === q.toLowerCase())
    if (liveFiltered.length === 0 && !already && allSelected.value.length < MAX_CAST) {
        emit('addCustomCast', q); search.value = ''
    }
}

function addCustom(name) { emit('addCustomCast', name); search.value = '' }

function clearAll() {
    props.selectedCast.forEach(n => emit('toggleCast', n))
    props.customCast.forEach(n => emit('removeCustomCast', n))
    lastShuffle.value = []
}

function handleSurprise() {
    if (remaining.value <= 0) return
    const alreadyPicked = new Set(allSelected.value.map(n => n.toLowerCase()))
    const justShuffled  = new Set(lastShuffle.value.map(n => n.toLowerCase()))
    let pool = CREATORS.value.filter(c => !alreadyPicked.has(c.name.toLowerCase()) && !justShuffled.has(c.name.toLowerCase()))
    if (pool.length < remaining.value) pool = CREATORS.value.filter(c => !alreadyPicked.has(c.name.toLowerCase()))
    const bag = [...pool]
    for (let i = bag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [bag[i], bag[j]] = [bag[j], bag[i]]
    }
    const picks = bag.slice(0, remaining.value)
    picks.forEach(p => emit('toggleCast', p.name))
    lastShuffle.value = picks.map(p => p.name)
}

function handleNext() {
    if (!hasSelection.value) { error.value = true } else { emit('next') }
}

</script>

<style scoped>
.step-wrap { display:flex; flex-direction:column; height:100%; padding:12px 16px 0; }
.step-header { margin-bottom:20px; }
.step-h1 { font-size:32px; font-weight:800; color:white; margin-bottom:6px; }
.step-lead { color:rgba(255,255,255,0.7); font-size:15px; }
.step-sub  { color:rgba(255,255,255,0.4); font-size:13px; margin-top:4px; }
.cast-inner { flex:1; display:flex; flex-direction:column; min-height:0; }
.cast-scroll { flex:1; min-height:0; overflow-y:auto; margin:0 -16px; padding:0 16px; -webkit-overflow-scrolling:touch; }
.cast-sticky { margin:0 -16px; padding:4px 16px 14px; flex-shrink:0; }
.search-wrap { position:relative; }
.search-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:rgba(255,255,255,0.4); font-size:15px; }
.search-input { width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:999px; padding:10px 16px 10px 40px; color:white; outline:none; font-size:16px !important; transition:border-color 0.2s; }
.search-input:focus { border-color:rgba(77,148,255,0.5); }
.selected-row { display:flex; gap:14px; flex-wrap:wrap; margin-top:12px; align-items:center; }
.avatar-btn { display:flex; flex-direction:column; align-items:center; gap:4px; background:none; border:none; cursor:pointer; padding:0; }
.avatar-slot { display:flex; flex-direction:column; align-items:center; gap:4px; }
.avatar-ring { width:44px; height:44px; border-radius:50%; overflow:hidden; }
.ring-selected { border:2px solid #4D94FF; }
.ring-custom { border:2px solid rgba(255,255,255,0.4); background:rgba(255,255,255,0.1); display:flex; align-items:center; justify-content:center; }
.ring-empty { border:2px dashed rgba(255,255,255,0.2); background:rgba(255,255,255,0.03); display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,0.3); }
.avatar-img { width:100%; height:100%; object-fit:cover; display:block; }
.avatar-initials { color:white; font-size:11px; font-weight:700; }
.avatar-label { font-size:11px; width:44px; text-align:center; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.text-brand-light { color:rgba(77,148,255,0.8); }
.text-muted { color:rgba(255,255,255,0.6); }
.clear-btn { margin-left:auto; background:none; border:none; color:rgba(255,255,255,0.4); font-size:12px; text-decoration:underline; cursor:pointer; min-height:44px; padding:0 8px; align-self:flex-start; }
.shuffle-row { display:flex; align-items:center; justify-content:space-between; margin-top:12px; }
.shuffle-btn { display:flex; align-items:center; gap:8px; background:rgba(0,82,204,0.15); border:1px solid rgba(0,82,204,0.4); color:#4D94FF; border-radius:999px; padding:8px 16px; font-size:13px; font-weight:500; cursor:pointer; min-height:44px; }
.shuffle-btn:disabled { opacity:0.4; cursor:not-allowed; }
.slots-label { font-size:12px; color:rgba(255,255,255,0.4); }
.add-random-row { margin-top:10px; display:flex; align-items:center; justify-content:space-between; }
.add-random-btn { display:flex; align-items:center; gap:6px; background:rgba(0,82,204,0.1); border:1px solid rgba(0,82,204,0.3); color:rgba(77,148,255,0.7); border-radius:999px; padding:6px 14px; font-size:12px; font-weight:500; cursor:pointer; min-height:40px; }
.popular-shelf { margin-bottom:20px; padding-top:8px; }
.shelf-label { font-size:11px; font-weight:700; color:rgba(255,255,255,0.5); text-transform:uppercase; letter-spacing:0.1em; margin-bottom:12px; }
.mt-4 { margin-top:16px; }
.popular-row { display:flex; gap:16px; overflow-x:auto; margin:0 -16px; padding:6px 16px 8px; }
.popular-item { display:flex; flex-direction:column; align-items:center; gap:6px; border:none; background:none; cursor:pointer; flex-shrink:0; padding:0; }
.popular-item:disabled { opacity:0.4; cursor:not-allowed; }
.popular-avatar { position:relative; width:56px; height:56px; border-radius:50%; overflow:hidden; }
.popular-selected { box-shadow:0 0 0 2px #4D94FF, 0 0 0 4px rgba(0,82,204,0.3); }
.popular-unselected { box-shadow:0 0 0 1px rgba(255,255,255,0.1); }
.popular-overlay { position:absolute; inset:0; background:rgba(0,82,204,0.5); }
.popular-check { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:white; font-weight:700; }
.popular-name { font-size:11px; text-align:center; width:56px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.shelf-divider { border-top:1px solid rgba(255,255,255,0.05); margin-top:16px; }
.zero-state { padding-bottom:8px; }
.custom-add-btn { width:100%; display:flex; align-items:center; gap:8px; background:rgba(0,82,204,0.15); border:1px solid rgba(0,82,204,0.4); border-radius:12px; padding:10px 14px; font-size:13px; color:#4D94FF; cursor:pointer; min-height:44px; }
.creator-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:14px; padding-bottom:20px; padding-top:4px; }
@media (min-width: 640px) { .creator-grid { grid-template-columns:repeat(4, 1fr); } }
.creator-card { position:relative; aspect-ratio:3/4; border-radius:16px; overflow:hidden; border:none; cursor:pointer; transition:transform 0.2s, opacity 0.2s; padding:0; }
.creator-disabled { cursor:not-allowed; }
.creator-dimmed { opacity:0.4; }
.creator-img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.creator-gradient { position:absolute; inset:0; background:linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.3), transparent); }
.creator-ring { position:absolute; inset:0; border:3px solid #4D94FF; border-radius:16px; pointer-events:none; }
.creator-check { position:absolute; top:8px; right:8px; width:24px; height:24px; border-radius:50%; background:#0052CC; display:flex; align-items:center; justify-content:center; color:white; font-size:12px; font-weight:700; }
.creator-info { position:absolute; bottom:0; left:0; padding:12px; }
.creator-name { color:white; font-weight:700; font-size:13px; line-height:1.2; }
.creator-role { color:rgba(255,255,255,0.7); font-size:11px; margin-top:2px; }
.step-footer { padding:14px 0 4px; flex-shrink:0; }
.error-msg { color:#f87171; font-size:13px; text-align:center; font-weight:500; margin-bottom:8px; }
.cta-btn { width:100%; background:#0052CC; color:white; font-weight:700; font-size:17px; padding:14px 0; border-radius:16px; border:none; cursor:pointer; transition:opacity 0.2s; }
.cta-btn:hover { opacity:0.9; }

/* Transitions */
.fade-height-enter-active, .fade-height-leave-active { transition:opacity 0.15s, max-height 0.15s; overflow:hidden; max-height:120px; }
.fade-height-enter-from, .fade-height-leave-to { opacity:0; max-height:0; }
.fade-enter-active, .fade-leave-active { transition:opacity 0.15s; }
.fade-enter-from, .fade-leave-to { opacity:0; }
</style>
